import React, { useState, useEffect } from 'react';
import { MdClose, MdAccessTime, MdDescription, MdEdit } from 'react-icons/md';
import {
  updateAssignment,
  deleteAssignment,
  publishAssignment,
  unpublishAssignment
} from '../../utils/api/AssignmentsApi';

const AssignmentDetailSidebar = ({ assignment: initialAssignment, classContext, onClose, onUpdate, onDelete, onOpenRubric, onBuildAssignment, onOpenComponent, onGradeSubmissions, onNavigate }) => {
  const [assignment, setAssignment] = useState(initialAssignment);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [environments, setEnvironments] = useState([]);

  // ERD Environment submission lookup state
  const [erdEnvData, setErdEnvData] = useState(null);       // { by_student, environment_ids }
  const [erdEnvLoading, setErdEnvLoading] = useState(false);
  const [erdEnvError, setErdEnvError] = useState(null);
  const [showErdEnvPanel, setShowErdEnvPanel] = useState(false);

  // Editable fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [lockAfterDueDate, setLockAfterDueDate] = useState(false);
  const [allowLateSubmission, setAllowLateSubmission] = useState(true);
  const [lateDueDate, setLateDueDate] = useState('');
  const [latePenaltyType, setLatePenaltyType] = useState('percentage');
  const [latePenaltyValue, setLatePenaltyValue] = useState(10);
  const [latePenaltyInterval, setLatePenaltyInterval] = useState('day');
  const [totalPoints, setTotalPoints] = useState(100);
  const [assignmentType, setAssignmentType] = useState('document');
  const [environmentId, setEnvironmentId] = useState('');

  useEffect(() => {
    if (initialAssignment) {
      setAssignment(initialAssignment);
      // Initialize editable fields
      setTitle(initialAssignment.title || '');
      setDescription(initialAssignment.description || '');
      setDueDate(initialAssignment.due_date ? new Date(initialAssignment.due_date).toISOString().slice(0, 16) : '');
      setLockAfterDueDate(initialAssignment.lock_after_due_date || false);
      setAllowLateSubmission(initialAssignment.allow_late_submission !== undefined ? initialAssignment.allow_late_submission : true);
      setLateDueDate(initialAssignment.late_due_date ? new Date(initialAssignment.late_due_date).toISOString().slice(0, 16) : '');
      setLatePenaltyType(initialAssignment.late_penalty_type || 'percentage');
      setLatePenaltyValue(initialAssignment.late_penalty_value || 10);
      setLatePenaltyInterval(initialAssignment.late_penalty_interval || 'day');
      setTotalPoints(initialAssignment.total_points || 100);
      setAssignmentType(initialAssignment.assignment_type || 'document');
      setEnvironmentId(initialAssignment.environment_id || '');
    }
  }, [initialAssignment]);

  // Fetch available environments
  useEffect(() => {
    if (!classContext?.class_code) return;
    fetch('/api/environments', {
      headers: { 'Content-Type': 'application/json', 'Class-Code': classContext.class_code },
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.environments) {
          setEnvironments(data.environments.filter(e => e !== 'None'));
        }
      })
      .catch(() => {});
  }, [classContext?.class_code]);

  const fetchErdEnvSubmissions = async (comp) => {
    if (!classContext?.class_id) return;
    setErdEnvLoading(true);
    setErdEnvError(null);
    try {
      const envIds = comp.content_ref?.environment_ids || [];
      const params = new URLSearchParams({ class_id: classContext.class_id });
      if (envIds.length) params.set('environment_ids', envIds.join(','));
      const res = await fetch(`/api/saved-works/class-erd?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setErdEnvData(data);
        setShowErdEnvPanel(true);
      } else {
        setErdEnvError(data.error || 'Failed to load ERD submissions');
      }
    } catch {
      setErdEnvError('Failed to load ERD submissions');
    } finally {
      setErdEnvLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const dataToSubmit = {
        title,
        description,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        total_points: Number(totalPoints),
        assignment_type: assignmentType,
        lock_after_due_date: lockAfterDueDate,
        allow_late_submission: allowLateSubmission,
        late_due_date: lateDueDate ? new Date(lateDueDate).toISOString() : null,
        late_penalty_type: latePenaltyType,
        late_penalty_value: Number(latePenaltyValue),
        late_penalty_interval: latePenaltyInterval,
        environment_id: environmentId || null
      };

      const result = await updateAssignment(assignment.assignment_id, dataToSubmit, classContext);
      setAssignment(result.assignment);
      setIsEditing(false);
      onUpdate && onUpdate(result.assignment);
    } catch (err) {
      console.error('Error updating assignment:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setTitle(assignment.title || '');
    setDescription(assignment.description || '');
    setDueDate(assignment.due_date ? new Date(assignment.due_date).toISOString().slice(0, 16) : '');
    setTotalPoints(assignment.total_points || 100);
    setAssignmentType(assignment.assignment_type || 'document');
    setLockAfterDueDate(assignment.lock_after_due_date || false);
    setAllowLateSubmission(assignment.allow_late_submission !== undefined ? assignment.allow_late_submission : true);
    setLateDueDate(assignment.late_due_date ? new Date(assignment.late_due_date).toISOString().slice(0, 16) : '');
    setLatePenaltyType(assignment.late_penalty_type || 'percentage');
    setLatePenaltyValue(assignment.late_penalty_value || 10);
    setLatePenaltyInterval(assignment.late_penalty_interval || 'day');
    setEnvironmentId(assignment.environment_id || '');
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this assignment? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      await deleteAssignment(assignment.assignment_id, classContext);
      onDelete && onDelete(assignment.assignment_id);
      onClose && onClose();
    } catch (err) {
      console.error('Error deleting assignment:', err);
      setError(err.message);
    }
  };

  const handleTogglePublish = async () => {
    try {
      setError(null);
      let result;

      if (assignment.is_published) {
        result = await unpublishAssignment(assignment.assignment_id, classContext);
      } else {
        result = await publishAssignment(assignment.assignment_id, classContext);
      }

      setAssignment(result.assignment);
      onUpdate && onUpdate(result.assignment);
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      erd: 'ERD',
      code: 'Code',
      document: 'Document'
    };
    return labels[type] || type.toUpperCase();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-gray-50 shadow-2xl z-50 overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900 m-0">{assignment.title}</h2>
            <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 uppercase tracking-wide">
              {getTypeLabel(assignment.assignment_type)}
            </span>
            <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-gray-100 text-gray-800">
              {assignment.total_points} pts
            </span>
            {assignment.is_published ? (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-700 uppercase tracking-wide">
                Published
              </span>
            ) : (
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 uppercase tracking-wide">
                Draft
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 transition-all duration-200 text-gray-600 hover:text-gray-900"
            aria-label="Close"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex gap-2 flex-wrap">
            {isEditing ? (
              <>
                <button
                  className="px-4 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-all duration-200 text-sm"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  className="px-4 py-2 rounded-lg font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-all duration-200 text-sm"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  className="px-4 py-2 rounded-lg font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-all duration-200 text-sm"
                  onClick={onOpenRubric}
                >
                  Edit Rubric
                </button>
                {assignment.is_published && onGradeSubmissions && (
                  <button
                    className="px-4 py-2 rounded-lg font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-all duration-200 text-sm"
                    onClick={onGradeSubmissions}
                  >
                    Grade Submissions
                  </button>
                )}
                <button
                  className="px-4 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 text-sm"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Settings
                </button>
                <button
                  className="px-4 py-2 rounded-lg font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200 text-sm"
                  onClick={handleTogglePublish}
                >
                  {assignment.is_published ? 'Unpublish' : 'Publish'}
                </button>
                <button
                  className="px-4 py-2 rounded-lg font-medium bg-white border border-gray-300 text-red-600 hover:bg-red-50 transition-all duration-200 text-sm"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-800 text-sm">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          {/* Due Date & Description - Compact */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <div className="space-y-3">
              {/* Due Date */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MdAccessTime className="text-lg text-gray-600" />
                  <h4 className="text-sm font-semibold text-gray-900 m-0">Due Date</h4>
                </div>
                {isEditing ? (
                  <input
                    type="datetime-local"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-sm text-gray-700 m-0 ml-6">{formatDate(assignment.due_date)}</p>
                )}
              </div>

              {/* Description */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <MdDescription className="text-lg text-gray-600" />
                  <h4 className="text-sm font-semibold text-gray-900 m-0">Description</h4>
                </div>
                {isEditing ? (
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px] resize-none"
                    placeholder="Brief description..."
                  />
                ) : (
                  <div className="text-sm text-gray-700 ml-6 line-clamp-3">
                    {assignment.description || 'No description provided.'}
                  </div>
                )}
              </div>

              {/* Linked Environment */}
              <div className="pt-3 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 m-0 mb-2">Linked Environment</h4>
                {isEditing ? (
                  <select
                    value={environmentId}
                    onChange={(e) => setEnvironmentId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— No environment —</option>
                    {environments.map(env => (
                      <option key={env} value={env}>{env}</option>
                    ))}
                  </select>
                ) : (
                  <div className="flex items-center gap-2">
                    {assignment.environment_id ? (
                      <>
                        <span className="px-2.5 py-1 bg-purple-50 border border-purple-200 rounded-lg text-xs font-semibold text-purple-700">
                          {assignment.environment_id}
                        </span>
                        <button
                          onClick={() => onNavigate && onNavigate('editor')}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          Edit in ERD Editor →
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-gray-500 m-0">No environment linked</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Components */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <h4 className="text-sm font-semibold text-gray-900 m-0 mb-3">Components</h4>
            <div className="space-y-2">
              {assignment.components && assignment.components.length > 0 ? (
                <>
                  {assignment.components.sort((a, b) => a.order - b.order).map((comp, idx) => {
                    const isErdEnv = comp.type === 'erd_env';
                    const colorClass =
                      comp.type === 'document' ? 'bg-blue-100 text-blue-700' :
                      comp.type === 'erd'       ? 'bg-purple-100 text-purple-700' :
                      isErdEnv                  ? 'bg-indigo-100 text-indigo-700' :
                      'bg-green-100 text-green-700';
                    const icon =
                      comp.type === 'document' ? 'DOC' :
                      comp.type === 'erd'       ? 'ERD' :
                      isErdEnv                  ? 'ENV' : 'CODE';
                    return (
                      <div key={comp.component_id || idx} className="space-y-1">
                        <div
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => isErdEnv ? fetchErdEnvSubmissions(comp) : (onOpenComponent ? onOpenComponent(comp) : onBuildAssignment())}
                        >
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${colorClass}`}>{icon}</span>
                          <span className="text-sm text-gray-800 font-medium flex-1">{comp.title}</span>
                          {isErdEnv && (
                            <span className="text-xs text-indigo-600">
                              {comp.content_ref?.all_environments
                                ? `All envs (${(comp.content_ref?.environment_ids || []).length})`
                                : comp.content_ref?.environment_id || '—'}
                            </span>
                          )}
                          <span className="text-xs text-gray-500">{comp.points} pts</span>
                          {isErdEnv
                            ? <span className="text-xs text-indigo-500 font-medium">View ERDs ›</span>
                            : <MdEdit className="text-gray-400 text-sm" />
                          }
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-between px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100 mt-1">
                    <span>Component total: {assignment.components.reduce((s, c) => s + (c.points || 0), 0)} pts</span>
                    <span>Assignment total: {assignment.total_points} pts</span>
                  </div>
                </>
              ) : (
                <div
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={onBuildAssignment}
                >
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">DOC</span>
                  <span className="text-sm text-gray-800 font-medium flex-1">Document</span>
                  <MdEdit className="text-gray-400 text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* ERD Environment Submissions Panel */}
          {showErdEnvPanel && (
            <div className="bg-white rounded-lg shadow-sm border border-indigo-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-indigo-900 m-0">ERD Submissions by Environment</h4>
                <button
                  className="text-xs text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer"
                  onClick={() => setShowErdEnvPanel(false)}
                >✕ Close</button>
              </div>

              {erdEnvLoading && (
                <p className="text-xs text-gray-500">Loading ERD submissions…</p>
              )}
              {erdEnvError && (
                <p className="text-xs text-red-600">{erdEnvError}</p>
              )}
              {erdEnvData && !erdEnvLoading && (() => {
                const { by_student, environment_ids } = erdEnvData;
                const students = Object.keys(by_student).sort();
                if (!students.length) {
                  return <p className="text-xs text-gray-500">No saved ERDs found yet.</p>;
                }
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-indigo-50">
                          <th className="text-left px-2 py-1.5 font-semibold text-indigo-800 border border-indigo-100">Student</th>
                          {environment_ids.map(envId => (
                            <th key={envId} className="text-center px-2 py-1.5 font-semibold text-indigo-800 border border-indigo-100 whitespace-nowrap">
                              {envId}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {students.map(username => (
                          <tr key={username} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5 font-medium text-gray-800 border border-gray-100 whitespace-nowrap">
                              {username}
                            </td>
                            {environment_ids.map(envId => {
                              const entry = by_student[username]?.[envId];
                              if (!entry) {
                                return (
                                  <td key={envId} className="px-2 py-1.5 text-center text-gray-300 border border-gray-100">—</td>
                                );
                              }
                              const ts = entry.timestamp ? new Date(entry.timestamp) : null;
                              return (
                                <td key={envId} className="px-2 py-1.5 text-center border border-gray-100">
                                  <div className="flex flex-col items-center gap-0.5">
                                    <span className="inline-block w-2 h-2 rounded-full bg-green-400" title="Saved" />
                                    <span className="text-gray-500 text-[10px]">
                                      {ts ? ts.toLocaleDateString() : ''}
                                    </span>
                                    {entry.input_text_preview && (
                                      <span
                                        className="text-indigo-600 cursor-pointer text-[10px] underline"
                                        title={entry.input_text_preview}
                                      >preview</span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[10px] text-gray-400 mt-2">
                      {students.length} student(s) × {environment_ids.length} environment(s). Green dot = has saved ERD. Hover preview for notation snippet.
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Late Submission Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-sm font-semibold text-gray-900 m-0">Late Submission</h4>
            </div>
            <div className="pl-6">
            {isEditing ? (
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer text-xs">
                  <input type="checkbox" checked={lockAfterDueDate} onChange={(e) => setLockAfterDueDate(e.target.checked)} className="mr-2" />
                  <span className="text-gray-700">Lock after due date</span>
                </label>
                {!lockAfterDueDate && (
                  <>
                    <label className="flex items-center cursor-pointer text-xs">
                      <input type="checkbox" checked={allowLateSubmission} onChange={(e) => setAllowLateSubmission(e.target.checked)} className="mr-2" />
                      <span className="text-gray-700">Allow late submissions</span>
                    </label>
                    {allowLateSubmission && (
                      <div className="ml-4 space-y-2 mt-2 p-2 bg-gray-50 rounded">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Late Due Date</label>
                          <input type="datetime-local" value={lateDueDate} onChange={(e) => setLateDueDate(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-xs" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Type</label>
                            <select value={latePenaltyType} onChange={(e) => setLatePenaltyType(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-xs">
                              <option value="percentage">%</option>
                              <option value="points">Pts</option>
                              <option value="flat">Flat</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Amount</label>
                            <input type="number" min="0" value={latePenaltyValue} onChange={(e) => setLatePenaltyValue(Number(e.target.value))} className="w-full px-2 py-1 border border-gray-300 rounded text-xs" />
                          </div>
                        </div>
                        {latePenaltyType !== 'flat' && (
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Interval</label>
                            <select value={latePenaltyInterval} onChange={(e) => setLatePenaltyInterval(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-xs">
                              <option value="hour">Hour</option>
                              <option value="day">Day</option>
                              <option value="week">Week</option>
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {lockAfterDueDate && <p className="text-xs text-gray-700 m-0">• Locked after due date</p>}
                {allowLateSubmission && lateDueDate && (
                  <p className="text-xs text-gray-700 m-0">
                    • Late until {formatDate(lateDueDate)}
                    {latePenaltyType && <span className="text-orange-600 ml-1">({latePenaltyValue}{latePenaltyType === 'percentage' ? '%' : 'pts'}/{latePenaltyInterval})</span>}
                  </p>
                )}
                {!allowLateSubmission && !lockAfterDueDate && <p className="text-xs text-red-600 m-0">• Late submissions not accepted</p>}
              </div>
            )}
            </div>
          </div>

        </div>
      </div>

      <style>
        {`
          @keyframes slide-in-right {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
          .animate-slide-in-right {
            animation: slide-in-right 0.3s ease-out;
          }
        `}
      </style>
    </>
  );
};

export default AssignmentDetailSidebar;
